import { useState, useCallback, useRef } from 'react';
import { getModuleMode } from '@/lib/feature-mode';
import { aiAssistantService } from '@/services/ai-assistant.service';
import { aiAssistantDemoService } from '@/services/demo/ai-assistant-demo.service';
import type { AIMessage, ToolCall } from '@/types/ai-assistant.types';

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

function getDataSource(): 'demo' | 'api' {
  const mode = getModuleMode('assets');
  return mode === 'demo' ? 'demo' : 'api';
}

interface UseAIAssistantChatOptions {
  onMessageReceived?: (message: AIMessage) => void;
  onError?: (error: Error) => void;
  onBackgroundResponse?: (sessionId: string, message: AIMessage) => void;
  getCurrentSessionId?: () => string | null;
}

interface UseAIAssistantChatReturn {
  messages: AIMessage[];
  isProcessing: boolean;
  currentTool: ToolCall | undefined;
  error: string | null;
  sendMessage: (content: string, context?: string) => Promise<void>;
  clearHistory: () => void;
  setMessages: React.Dispatch<React.SetStateAction<AIMessage[]>>;
  cancelProcessing: () => void;
}

const STORAGE_KEY_PREFIX = 'ai-assistant-session-';
const DEFAULT_STORAGE_KEY = 'ai-assistant-messages';

function getStorageKey(sessionId: string | null): string {
  return sessionId ? `${STORAGE_KEY_PREFIX}${sessionId}` : DEFAULT_STORAGE_KEY;
}

function loadMessagesFromStorage(sessionId: string | null = null): AIMessage[] {
  try {
    const stored = localStorage.getItem(getStorageKey(sessionId));
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveMessagesToStorage(messages: AIMessage[], sessionId: string | null = null): void {
  try {
    const key = getStorageKey(sessionId);
    if (messages.length > 0) {
      localStorage.setItem(key, JSON.stringify(messages));
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    // Ignore storage errors
  }
}

function deleteSessionFromStorage(sessionId: string): void {
  try {
    localStorage.removeItem(getStorageKey(sessionId));
  } catch {
    // Ignore storage errors
  }
}

// Export storage utilities for use in context
export { loadMessagesFromStorage, saveMessagesToStorage, deleteSessionFromStorage };

export function useAIAssistantChat(
  options: UseAIAssistantChatOptions = {}
): UseAIAssistantChatReturn {
  const { onMessageReceived, onError, onBackgroundResponse, getCurrentSessionId } = options;
  
  const [messages, setMessages] = useState<AIMessage[]>(loadMessagesFromStorage);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentTool, setCurrentTool] = useState<ToolCall | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  
  const abortControllerRef = useRef<AbortController | null>(null);
  // Track which session the current request belongs to
  const requestSessionIdRef = useRef<string | null>(null);

  const sendMessage = useCallback(async (content: string, context?: string) => {
    if (!content.trim()) return;

    // Don't cancel pending requests - let them complete in the background
    // Just create a new abort controller for this request
    abortControllerRef.current = new AbortController();
    
    // Track which session this request belongs to
    const requestSessionId = getCurrentSessionId?.() ?? null;
    requestSessionIdRef.current = requestSessionId;

    const userMessage: AIMessage = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      timestamp: Date.now(),
    };

    setMessages(prev => {
      const updated = [...prev, userMessage];
      saveMessagesToStorage(updated);
      return updated;
    });
    setIsProcessing(true);
    setError(null);

    try {
      const isDemo = getDataSource() === 'demo';
      const service = isDemo ? aiAssistantDemoService : aiAssistantService;
      
      const history = messagesRef.current
        .filter(msg => msg.role === 'user' || msg.role === 'assistant')
        .map(msg => ({
          role: msg.role,
          content: msg.content,
        }));

      const response = await service.sendMessage({
        message: content.trim(),
        history,
        context,
      }, abortControllerRef.current.signal);

      const assistantMessage: AIMessage = {
        id: response.id,
        role: 'assistant',
        content: response.content,
        timestamp: new Date(response.timestamp).getTime(),
      };

      if (response.toolCall) {
        const toolCall: ToolCall = {
          id: response.toolCall.id,
          name: response.toolCall.name,
          status: response.toolCall.status as ToolCall['status'],
          result: response.toolCall.result,
        };
        assistantMessage.toolCall = toolCall;
        setCurrentTool(toolCall);
      }

      // Check if user switched to a different session while request was in progress
      const currentSessionId = getCurrentSessionId?.() ?? null;
      const sessionChanged = requestSessionId !== currentSessionId;
      
      if (sessionChanged && requestSessionId) {
        // User switched sessions - save response to the original session via callback
        onBackgroundResponse?.(requestSessionId, assistantMessage);
      } else {
        // Still on the same session - update UI normally
        setMessages(prev => {
          const updated = [...prev, assistantMessage];
          saveMessagesToStorage(updated);
          return updated;
        });
        onMessageReceived?.(assistantMessage);
      }
    } catch (err) {
      // Ignore abort errors - these are expected when switching sessions
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      onError?.(err instanceof Error ? err : new Error(errorMessage));

      const errorResponse: AIMessage = {
        id: generateId(),
        role: 'assistant',
        content: 'I apologize, but I encountered an error processing your request. Please try again.',
        timestamp: Date.now(),
      };

      setMessages(prev => {
        const updated = [...prev, errorResponse];
        saveMessagesToStorage(updated);
        return updated;
      });
    } finally {
      setIsProcessing(false);
      setCurrentTool(undefined);
    }
  }, [onMessageReceived, onError, onBackgroundResponse, getCurrentSessionId]);

  const clearHistory = useCallback(() => {
    setMessages([]);
    saveMessagesToStorage([]);
    setError(null);
    setCurrentTool(undefined);
  }, []);

  const cancelProcessing = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsProcessing(false);
    setCurrentTool(undefined);
  }, []);

  return {
    messages,
    isProcessing,
    currentTool,
    error,
    sendMessage,
    clearHistory,
    setMessages,
    cancelProcessing,
  };
}
